import React from 'react';
import {
  ArrowRight, BrainCircuit, Cpu, PenTool, Database, Megaphone,
  GraduationCap, Plus, Check, Wallet,
  Instagram, Linkedin, Github, Twitter, Link2, X,
  Upload, ImageIcon, Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { TRACKS, DURATIONS, EDUCATION_LEVELS, SALARY, skillsForTrack, skillTestQuestions } from '@/data/flow';
import { formatIDR } from '@/lib/storage';
import { cn } from '@/lib/utils';

const ICONS = { BrainCircuit, Cpu, PenTool, Database, Megaphone };

// A controlled field. value/onChange managed by the wizard.
// onEnter -> advance for text inputs; onAutoAdvance(v) -> select then advance.
export const WizardField = ({ step, value, answers, onChange, onEnter, onAutoAdvance }) => {
  switch (step.type) {
    case 'text':
    case 'email':
    case 'tel':
    case 'link':
      return (
        <Input
          autoFocus
          type={step.type === 'tel' ? 'tel' : step.type === 'email' ? 'email' : 'text'}
          inputMode={step.type === 'tel' ? 'numeric' : undefined}
          value={value || ''}
          placeholder={step.placeholder}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onEnter()}
          className="h-14 rounded-xl border-border bg-background px-4 text-lg shadow-sm focus-visible:ring-2 focus-visible:ring-primary/40"
        />
      );

    case 'longtext':
      return (
        <Textarea
          autoFocus
          rows={5}
          value={value || ''}
          placeholder={step.placeholder}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onEnter(); }}
          className="resize-none rounded-xl border-border bg-background p-4 text-base shadow-sm focus-visible:ring-2 focus-visible:ring-primary/40"
        />
      );

    case 'education':
      return <EducationField value={value} onChange={onChange} />;

    case 'photo':
      return <PhotoField value={value} onChange={onChange} />;

    case 'social':
      return <SocialField value={value} onChange={onChange} />;

    case 'links':
      return <LinksField value={value} onChange={onChange} />;

    case 'single-track':
      return <TrackField value={value} onAutoAdvance={onAutoAdvance} />;

    case 'multi-skill':
      return <SkillField answers={answers} value={value} onChange={onChange} />;

    case 'skill-test':
      return <SkillTestField answers={answers} value={value} onChange={onChange} />;

    case 'single-duration':
      return <DurationField value={value} onAutoAdvance={onAutoAdvance} />;

    case 'salary':
      return <SalaryField value={value} onChange={onChange} />;

    default:
      return null;
  }
};

function EducationField({ value, onChange }) {
  const v = value || { level: '', institution: '', major: '', year: '' };
  const set = (patch) => onChange({ ...v, ...patch });
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Select value={v.level} onValueChange={(level) => set({ level })}>
        <SelectTrigger className="h-12 rounded-xl border-border bg-background">
          <SelectValue placeholder="Jenjang pendidikan saat ini / terakhir" />
        </SelectTrigger>
        <SelectContent>
          {EDUCATION_LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
      <Input
        value={v.year}
        placeholder="Angkatan / lulus (mis. 2025)"
        onChange={(e) => set({ year: e.target.value })}
        className="h-12 rounded-xl border-border bg-background"
      />
      <Input
        value={v.institution}
        placeholder="Nama sekolah / kampus"
        onChange={(e) => set({ institution: e.target.value })}
        className="h-12 rounded-xl border-border bg-background sm:col-span-2"
      />
      <Input
        value={v.major}
        placeholder="Jurusan / program studi"
        onChange={(e) => set({ major: e.target.value })}
        className="h-12 rounded-xl border-border bg-background sm:col-span-2"
      />
    </div>
  );
}

const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

function PhotoField({ value, onChange }) {
  const inputRef = React.useRef(null);
  const [err, setErr] = React.useState(null);
  // Note: intentionally not revoking the object URL on cleanup — under React
  // StrictMode the double-invoke would revoke a URL still in use and break the
  // preview. The few blob URLs are released when the page navigates away.
  const previewUrl = React.useMemo(
    () => (value instanceof File ? URL.createObjectURL(value) : null),
    [value],
  );

  const pick = (file) => {
    if (!file) return;
    const okType = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || /\.(jpe?g|png|webp)$/i.test(file.name);
    if (!okType) { setErr('Format harus JPG, PNG, atau WEBP.'); return; }
    if (file.size > MAX_PHOTO_BYTES) { setErr('Ukuran foto maksimal 5MB.'); return; }
    setErr(null);
    onChange(file);
  };

  return (
    <div className="space-y-3">
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }}
      />

      {value instanceof File ? (
        <div className="flex items-center gap-4 rounded-2xl border border-border bg-secondary/30 p-4">
          <img src={previewUrl} alt="Pratinjau foto profil" className="h-20 w-20 rounded-xl object-cover ring-1 ring-border" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">{value.name}</p>
            <p className="text-xs text-muted-foreground">{(value.size / 1024).toFixed(0)} KB</p>
            <div className="mt-2 flex gap-2">
              <Button variant="outlineBrand" size="sm" className="rounded-lg" onClick={() => inputRef.current?.click()}>
                <ImageIcon className="h-3.5 w-3.5" /> Ganti
              </Button>
              <Button variant="ghost" size="sm" className="rounded-lg text-muted-foreground hover:text-destructive" onClick={() => onChange(null)}>
                <Trash2 className="h-3.5 w-3.5" /> Hapus
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <button
          onClick={() => inputRef.current?.click()}
          className="flex w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border bg-secondary/20 p-8 text-center transition-colors hover:border-primary/50 hover:bg-secondary/40"
        >
          <span className="grid h-12 w-12 place-items-center rounded-full bg-primary-soft text-primary">
            <Upload className="h-5 w-5" />
          </span>
          <span className="text-sm font-medium text-foreground">Klik untuk unggah foto</span>
          <span className="text-xs text-muted-foreground">JPG, PNG, atau WEBP · maks 5MB</span>
        </button>
      )}

      {err && <p className="text-sm font-medium text-destructive">{err}</p>}
    </div>
  );
}

function SkillTestField({ answers, value, onChange }) {
  const questions = skillTestQuestions(answers.track);
  const getAns = (i) => (value && value[i] && value[i].answer) || '';
  const setAns = (i, text) => {
    onChange(questions.map((q, idx) => ({ question: q, answer: idx === i ? text : getAns(idx) })));
  };
  return (
    <div className="space-y-5">
      {questions.map((q, i) => (
        <div key={i}>
          <label className="flex items-start gap-2 text-sm font-medium text-foreground">
            <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary-soft text-xs font-bold text-primary">{i + 1}</span>
            <span>{q}</span>
          </label>
          <Textarea
            value={getAns(i)}
            onChange={(e) => setAns(i, e.target.value)}
            rows={2}
            placeholder="Jawabanmu..."
            className="mt-2 resize-none rounded-xl border-border bg-background p-3 text-sm focus-visible:ring-2 focus-visible:ring-primary/40"
          />
        </div>
      ))}
    </div>
  );
}

const SOCIALS = [
  { key: 'instagram', icon: Instagram, prefix: '@', placeholder: 'username' },
  { key: 'linkedin', icon: Linkedin, prefix: '', placeholder: 'linkedin.com/in/...' },
  { key: 'github', icon: Github, prefix: '@', placeholder: 'username' },
  { key: 'twitter', icon: Twitter, prefix: '@', placeholder: 'username' },
];

function SocialField({ value, onChange }) {
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {SOCIALS.map((s) => (
        <div
          key={s.key}
          className="flex h-12 w-full min-w-0 items-center gap-2 rounded-xl border border-border bg-background px-3 transition-shadow focus-within:ring-2 focus-within:ring-primary/40"
        >
          <s.icon className="h-4 w-4 shrink-0 text-primary" />
          {s.prefix && <span className="shrink-0 text-sm text-muted-foreground">{s.prefix}</span>}
          <input
            value={v[s.key] || ''}
            placeholder={s.placeholder}
            onChange={(e) => set(s.key, e.target.value)}
            className="h-full w-full min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
      ))}
    </div>
  );
}

function LinksField({ value, onChange }) {
  const [rows, setRows] = React.useState(() => (value && value.length ? value : ['']));
  const commit = (next) => {
    setRows(next);
    onChange(next.map((x) => x.trim()).filter(Boolean));
  };
  return (
    <div className="space-y-3">
      {rows.map((row, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="flex h-12 min-w-0 flex-1 items-center gap-2 rounded-xl border border-border bg-background px-3 transition-shadow focus-within:ring-2 focus-within:ring-primary/40">
            <Link2 className="h-4 w-4 shrink-0 text-primary" />
            <input
              value={row}
              placeholder="https://..."
              onChange={(e) => { const n = [...rows]; n[i] = e.target.value; commit(n); }}
              className="h-full w-full min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
            />
          </div>
          {rows.length > 1 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => { const n = rows.filter((_, j) => j !== i); commit(n.length ? n : ['']); }}
              className="h-11 w-11 shrink-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      ))}
      <Button variant="outlineBrand" onClick={() => setRows([...rows, ''])} className="rounded-xl">
        <Plus className="h-4 w-4" /> Tambah tautan
      </Button>
    </div>
  );
}

function TrackField({ value, onAutoAdvance }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {TRACKS.map((t) => {
        const Icon = ICONS[t.icon];
        const active = value === t.value;
        return (
          <button
            key={t.value}
            onClick={() => onAutoAdvance(t.value)}
            className={cn(
              'group flex items-start gap-3 rounded-2xl border p-4 text-left transition-all',
              active
                ? 'border-primary bg-primary-soft ring-1 ring-primary/30'
                : 'border-border bg-background hover:border-primary/40 hover:shadow-card'
            )}
          >
            <span className={cn('grid h-11 w-11 shrink-0 place-items-center rounded-xl transition-colors',
              active ? 'bg-gradient-brand text-primary-foreground' : 'bg-secondary text-primary')}>
              <Icon className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block font-display text-base font-semibold text-foreground">{t.label}</span>
              <span className="mt-0.5 block text-sm leading-snug text-muted-foreground">{t.desc}</span>
            </span>
            {active && <Check className="ml-auto h-5 w-5 shrink-0 text-primary" strokeWidth={2.5} />}
          </button>
        );
      })}
    </div>
  );
}

function SkillField({ answers, value, onChange }) {
  const selected = value || [];
  const suggestions = skillsForTrack(answers.track);
  const [custom, setCustom] = React.useState('');
  const all = [...new Set([...suggestions, ...selected])];

  const toggle = (s) =>
    onChange(selected.includes(s) ? selected.filter((x) => x !== s) : [...selected, s]);
  const addCustom = () => {
    const c = custom.trim();
    if (c && !selected.includes(c)) onChange([...selected, c]);
    setCustom('');
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {all.map((s) => {
          const active = selected.includes(s);
          return (
            <button
              key={s}
              onClick={() => toggle(s)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-medium transition-colors',
                active
                  ? 'border-transparent bg-gradient-brand text-primary-foreground shadow-glow'
                  : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground'
              )}
            >
              {active && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
              {s}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <Input
          value={custom}
          placeholder="Tambah skill lain..."
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addCustom()}
          className="h-11 flex-1 rounded-xl border-border bg-background text-sm"
        />
        <Button onClick={addCustom} variant="outlineBrand" size="icon" className="h-11 w-11 shrink-0 rounded-xl">
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        {selected.length > 0 ? `${selected.length} skill dipilih` : 'Pilih minimal satu skill yang kamu kuasai.'}
      </p>
    </div>
  );
}

function DurationField({ value, onAutoAdvance }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {DURATIONS.map((d) => {
        const active = value === d.value;
        return (
          <button
            key={d.value}
            onClick={() => onAutoAdvance(d.value)}
            className={cn(
              'rounded-2xl border p-5 text-left transition-all',
              active ? 'border-primary bg-primary-soft ring-1 ring-primary/30' : 'border-border bg-background hover:border-primary/40 hover:shadow-card'
            )}
          >
            <span className="font-display text-lg font-semibold text-foreground">{d.label}</span>
            <span className="mt-1 block text-sm text-muted-foreground">{d.hint}</span>
          </button>
        );
      })}
    </div>
  );
}

function SalaryField({ value, onChange }) {
  const flexible = value === null;
  const amount = typeof value === 'number' ? value : SALARY.default;
  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Wallet className="h-4 w-4 text-primary" /> Per bulan
        </div>
        <span className={cn('font-display text-3xl font-bold', flexible ? 'text-muted-foreground' : 'text-gradient-brand')}>
          {flexible ? 'Fleksibel' : formatIDR(amount)}
        </span>
      </div>
      <Slider
        value={[amount]}
        min={SALARY.min}
        max={SALARY.max}
        step={SALARY.step}
        disabled={flexible}
        onValueChange={(arr) => onChange(arr[0])}
        className={cn(flexible && 'pointer-events-none opacity-40')}
      />
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{formatIDR(SALARY.min)}</span>
        <span>{formatIDR(SALARY.max)}+</span>
      </div>
      <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-border bg-secondary/50 p-4">
        <Checkbox
          checked={flexible}
          onCheckedChange={(c) => onChange(c ? null : SALARY.default)}
          className="data-[state=checked]:border-primary data-[state=checked]:bg-primary"
        />
        <span className="text-sm text-foreground">Aku fleksibel, sesuai kebijakan perusahaan</span>
      </label>
    </div>
  );
}

export const NextArrow = ArrowRight;
export const EduIcon = GraduationCap;
